import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useCollectionPushes } from '_/hooks/useCollections';
import { formatDateTime } from '_/utils/dateUtils';
import zeusSdk from '_/utils/SDKs';
import { Tag, Table, message, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useEffect, useCallback } from 'react';
import ReactJson from 'react-json-view';

interface ICollectionPush {
  id: string;
  source: string;
  destination: string;
  addedAt: string;
  completedAt: string;
  sourceSlug: string;
  snapshotId: string;
  metadata: any;
  status: string;
  error: string;
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETE: 'success',
  FAILED: 'error',
  IN_PROGRESS: 'processing',
  PENDING: 'default',
};

function CollectionPushes(): ReactElement {
  const { selectedCollection, selectedCollectionSlug } = useCollection();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();

  const { data: pushes = [], isLoading, isRefetching, refetch } = useCollectionPushes(selectedCollection?.id);

  const handleRevert = async (pushId: string) => {
    try {
      await zeusSdk.destinationPushes.revert(pushId);
      message.success('Push reverted successfully');
      void refetch();
    } catch {
      message.error('Failed to revert push');
    }
  };

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, isRefetching, handleRefresh]);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push(
        {
          title: selectedCollection.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
        },
        { title: 'Pushes' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
  ]);

  const columns: ColumnsType<ICollectionPush> = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
    },
    {
      title: 'Started at',
      dataIndex: 'addedAt',
      key: 'addedAt',
      render: (val: string) => formatDateTime(val),
      sorter: (a: ICollectionPush, b: ICollectionPush) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime(),
    },
    {
      title: 'Completed at',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (val: string) => formatDateTime(val),
      sorter: (a: ICollectionPush, b: ICollectionPush) => new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime(),
    },
    {
      title: 'Snapshot',
      dataIndex: 'snapshotId',
      key: 'snapshotId',
      render: (snapshotId: string, record: ICollectionPush) =>
        snapshotId ? (
          <a
            href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${record.sourceSlug}/snapshots/${snapshotId}`}
            target="_blank"
            rel="noreferrer"
          >
            {snapshotId}
          </a>
        ) : null,
    },
    {
      title: 'Meta-data',
      dataIndex: 'metadata',
      key: 'metadata',
      render: (metadata: any) =>
        metadata ? (
          <ReactJson src={metadata} collapsed={true} displayDataTypes={false} displayObjectSize={false} name={false} />
        ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
      filters: Array.from(new Set((pushes as ICollectionPush[]).map((p) => p.status))).map((s) => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: ICollectionPush) =>
        record.status === 'COMPLETE' ? (
          <Button size="small" onClick={() => void handleRevert(record.id)}>
            Revert
          </Button>
        ) : null,
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Table
      dataSource={pushes as ICollectionPush[]}
      columns={columns}
      rowKey={(record: ICollectionPush) => record.id}
      scroll={{ x: true }}
      pagination={{
        pageSize: 50,
        showSizeChanger: false,
        showTotal: (total) => `Total ${total} pushes`,
      }}
    />
  );
}

export default CollectionPushes;
